import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';
import AnimatedCirclesBackground from '@/components/AnimatedCirclesBackground';

export type HeadlineDescriptionCtaStepsProps =
  SliceComponentProps<Content.HeadlineDescriptionCtaStepsSlice>;

const HeadlineDescriptionCtaSteps = ({
  slice,
}: HeadlineDescriptionCtaStepsProps): JSX.Element => {
  // Separate handling for the two variations to fix TypeScript errors
  if (slice.variation === 'stepsWithIcon') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white py-20 md:py-32 overflow-hidden"
      >
        <AnimatedCirclesBackground />
        <div className="relative container px-4 md:px-6">
          <div
            id="headline-and-cta"
            className="max-w-3xl text-start flex flex-col items-start gap-6"
          >
            <PrismicRichText
              field={slice.primary.headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-4xl md:text-5xl font-bold">{children}</h2>
                ),
              }}
            />
            <PrismicRichText
              field={slice.primary.summary}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg text-white/80">{children}</p>
                ),
              }}
            />
            <PrismicNextLink
              field={slice.primary.primary_cta}
              className="secondary-button"
            >
              <ArrowRightIcon className="arrow-icon" />
              Discover how VelX drives outcomes
            </PrismicNextLink>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {slice.primary.steps.map((item, index) => (
              <div
                key={index}
                className={`h-full flex flex-col rounded-2xl p-8  ${
                  index === 1 ? 'md:mt-8' : index === 2 ? 'md:mt-16' : ''
                }`}
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
                }}
              >
                {isFilled.image(item.step_icon) && (
                  <PrismicNextImage
                    field={item.step_icon}
                    className="h-8 w-8 mb-4"
                  />
                )}
                <PrismicRichText
                  field={item.step_title}
                  components={{
                    heading3: ({ children }) => (
                      <h3 className="text-2xl font-semibold text-white mt-2">
                        {children}
                      </h3>
                    ),
                  }}
                />
                <PrismicRichText
                  field={item.step_description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-white/80 mt-4">{children}</p>
                    ),
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Default variation
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white md:pb-24 bg-[#0F423A]"
    >
      <div className="absolute top-0 left-0 w-full h-52 md:h-52 z-20 bg-gradient-to-b from-[#0E1010] to-transparent pointer-events-none" />
      <div className="container mx-auto px-4 md:px-6">
        <div
          id="headline-and-cta"
          className="max-w-5xl text-start flex flex-col items-start gap-6"
        >
          <PrismicRichText
            field={slice.primary.headline}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl md:text-5xl font-bold mt-20 z-20">
                  {children}
                </h2>
              ),
            }}
          />
          <PrismicRichText
            field={slice.primary.summary}
            components={{
              paragraph: ({ children }) => (
                <p className="text-lg text-white/80">{children}</p>
              ),
            }}
          />
          <PrismicNextLink
            field={slice.primary.primary_cta}
            className="secondary-button"
          >
            <ArrowRightIcon className="arrow-icon" />
            Discover how VelX drives outcomes
          </PrismicNextLink>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {slice.primary.steps.map((item, index) => (
            <div
              key={index}
              className={`h-full flex flex-col rounded-2xl p-8 ${
                index === 1 ? 'md:mt-8' : index === 2 ? 'md:mt-16' : ''
              }`}
              style={{
                background:
                  'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <p className="text-sm text-white/60 uppercase">
                {item.step_label}
              </p>
              <PrismicRichText
                field={item.step_title}
                components={{
                  heading3: ({ children }) => (
                    <h3 className="text-2xl font-semibold text-green-400 mt-2">
                      {children}
                    </h3>
                  ),
                }}
              />
              <PrismicRichText
                field={item.step_description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-white/80 mt-4">{children}</p>
                  ),
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeadlineDescriptionCtaSteps;
